import { useMemo } from "react";
import { useChosenDepartment } from "../../../../contexts/ChosenDepartment";
import { usePredictCostBatch } from "../../../../hooks/usePredictCost";
import { useRosterLookup } from "../../../../hooks/useRosterLookup";
import { buildCostPrediction } from "../../../../utils/costPrediction";
import type { BaseRequest } from "../../../../utils/models";
import type { GapsAcceptedHours } from "../openShifts/gapsOpenShiftsModel";
import { GapsPredictCostSource, useReportPredictCostFailure } from "../useReportPredictCostFailure";

/**
 * The overtime this approval would add, predicted on the hours it WRITES — a partial pickup's accepted part, not the
 * posted shift — so the confirm states what the gates will check. One person, one request; the same batch endpoint the
 * list uses. `undefined` hours when the prediction is unavailable, which the card says as "not known".
 */
export const useApprovalOvertime = (request: BaseRequest, nurseId: number, hours: GapsAcceptedHours): { hours: number | undefined; isLoading: boolean } => {
	const { chosenDepartment } = useChosenDepartment();
	const { getNurseById } = useRosterLookup(chosenDepartment.department_id);
	const startISO = hours.start.toISO();
	const endISO = hours.end.toISO();
	const params = useMemo(
		() =>
			startISO === null || endISO === null
				? null
				: {
						departmentId: chosenDepartment.department_id,
						nurseIds: [nurseId],
						startTime: startISO,
						endTime: endISO,
						...(request.job_id === undefined ? {} : { jobId: request.job_id }),
					},
		[chosenDepartment.department_id, nurseId, startISO, endISO, request.job_id],
	);
	const batch = usePredictCostBatch(params, 1);
	useReportPredictCostFailure(batch, { source: GapsPredictCostSource.Approve, departmentId: chosenDepartment.department_id, nurseCount: 1 });
	const prediction = batch.predictions[nurseId];
	return {
		hours:
			prediction === undefined ? undefined : buildCostPrediction(prediction, { isGlobalSalary: Boolean(getNurseById(nurseId)?.isGlobalSalary) }).overtimeHours,
		isLoading: params !== null && batch.isLoading,
	};
};
